/**
 * PURPOSE: Administrar el medallero del evento desde Winscore.
 * RESPONSIBILITIES: Seleccionar deporte, editar oro/plata/bronce por institución y mostrar vista previa general.
 * COLLABORATORS: hayllisV2MedalService.js, ListCategoriasSection.jsx y backend/src/hayllis-v2/medals.
 * ROUTE: src/area/MedalTableArea/MedalSportEditor.jsx
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { FaMedal, FaSave, FaSyncAlt, FaTrophy } from "react-icons/fa";

import {
  getEventMedalEditor,
  updateEventMedalsBulk,
} from "../../services/hayllisV2MedalService.js";

const toSafeInt = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : 0;
};

const medalColumns = [
  { key: "gold", label: "Oro", short: "O", className: "text-amber-700" },
  { key: "silver", label: "Plata", short: "P", className: "text-slate-700" },
  { key: "bronze", label: "Bronce", short: "B", className: "text-orange-700" },
];

const MedalSportEditor = () => {
  const { idevent } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sports, setSports] = useState([]);
  const [selectedSportId, setSelectedSportId] = useState("");
  const [institutions, setInstitutions] = useState([]);
  const [summary, setSummary] = useState([]);

  const loadEditor = useCallback(
    async (sportId = selectedSportId) => {
      if (!idevent) return;

      setLoading(true);
      try {
        const payload = await getEventMedalEditor({ idevent, idsport: sportId || undefined });
        const nextSports = Array.isArray(payload?.sports) ? payload.sports : [];
        const nextSelectedSportId = String(payload?.selectedSportId || sportId || nextSports[0]?.id || "");
        const nextInstitutions = Array.isArray(payload?.institutions) ? payload.institutions : [];

        setSports(nextSports);
        setSelectedSportId(nextSelectedSportId);
        setInstitutions(
          nextInstitutions.map((institution) => ({
            ...institution,
            medals: {
              gold: toSafeInt(institution?.medals?.gold),
              silver: toSafeInt(institution?.medals?.silver),
              bronze: toSafeInt(institution?.medals?.bronze),
            },
          }))
        );
        setSummary(Array.isArray(payload?.summary) ? payload.summary : []);
      } catch (error) {
        toast.error("No se pudo cargar el editor del medallero");
        console.error("Error loading medal editor", error);
      } finally {
        setLoading(false);
      }
    },
    [idevent, selectedSportId]
  );

  useEffect(() => {
    loadEditor("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idevent]);

  const selectedSport = useMemo(() => {
    return sports.find((sport) => String(sport.id) === String(selectedSportId));
  }, [sports, selectedSportId]);

  const handleSportChange = (event) => {
    const nextSportId = event.target.value;
    setSelectedSportId(nextSportId);
    loadEditor(nextSportId);
  };

  const updateInstitutionMedal = (institutionId, medalKey, value) => {
    setInstitutions((current) =>
      current.map((institution) => {
        if (String(institution.id) !== String(institutionId)) return institution;
        return {
          ...institution,
          medals: {
            ...institution.medals,
            [medalKey]: toSafeInt(value),
          },
        };
      })
    );
  };

  const handleSave = async () => {
    if (!selectedSportId) {
      toast.error("Selecciona un deporte para guardar el medallero");
      return;
    }

    setSaving(true);
    try {
      const payload = await updateEventMedalsBulk({
        idevent,
        idsport: selectedSportId,
        items: institutions.map((institution) => ({
          idinstitution: institution.id,
          gold: toSafeInt(institution.medals?.gold),
          silver: toSafeInt(institution.medals?.silver),
          bronze: toSafeInt(institution.medals?.bronze),
        })),
      });

      setSummary(Array.isArray(payload?.summary) ? payload.summary : []);
      toast.success("¡Medallero guardado correctamente!");
      await loadEditor(selectedSportId);
    } catch (error) {
      toast.error("No se pudo guardar el medallero");
      console.error("Error saving medal table", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="grid gap-6">
      <div className="rounded-2xl border border-blue-100 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-600 text-white">
                <FaMedal />
              </span>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Medallero del evento</h2>
                <p className="text-sm text-gray-500">
                  Edita las medallas por deporte e institución. Hayllis 2.0 mostrará el resumen general y detalle por disciplina.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <select
              value={selectedSportId}
              onChange={handleSportChange}
              disabled={loading || saving}
              className="min-w-[240px] rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-gray-100"
            >
              <option value="">Selecciona deporte</option>
              {sports.map((sport) => (
                <option key={sport.id} value={sport.id}>
                  {sport.displayName || sport.name}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={() => loadEditor(selectedSportId)}
              disabled={loading || saving}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-200 px-4 py-3 text-sm font-bold text-gray-700 transition hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FaSyncAlt className={loading ? "animate-spin" : ""} />
              Recargar
            </button>

            <button
              type="button"
              onClick={handleSave}
              disabled={loading || saving || !selectedSportId}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FaSave />
              {saving ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {selectedSport ? `Editar ${selectedSport.displayName || selectedSport.name}` : "Editor por deporte"}
            </h3>
            <p className="text-sm text-gray-500">Solo aparecen instituciones participantes del deporte seleccionado.</p>
          </div>
          {loading ? <span className="text-sm font-semibold text-blue-600">Cargando...</span> : null}
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[720px] w-full text-left">
            <thead className="bg-gray-50 text-xs font-bold uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">Institución</th>
                {medalColumns.map((column) => (
                  <th key={column.key} className="px-4 py-3 text-center">
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {institutions.length ? (
                institutions.map((institution) => (
                  <tr key={institution.id}>
                    <td className="px-4 py-3">
                      <div className="font-bold text-gray-900">{institution.name}</div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">{institution.abbreviation}</div>
                    </td>
                    {medalColumns.map((column) => (
                      <td key={column.key} className="px-4 py-3 text-center">
                        <input
                          type="number"
                          min="0"
                          value={institution.medals?.[column.key] ?? 0}
                          onChange={(event) => updateInstitutionMedal(institution.id, column.key, event.target.value)}
                          disabled={loading || saving}
                          className={`mx-auto w-24 rounded-lg border border-gray-300 px-3 py-2 text-center text-lg font-bold outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-gray-100 ${column.className}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-sm font-semibold text-gray-500">
                    {selectedSportId ? "No hay instituciones participantes para este deporte." : "Selecciona un deporte para empezar."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
            <FaTrophy />
          </span>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Vista previa general</h3>
            <p className="text-sm text-gray-500">Orden público: oro, plata, bronce y total.</p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[620px] w-full text-left">
            <thead className="bg-gray-50 text-xs font-bold uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">Institución</th>
                <th className="px-4 py-3 text-center">Oro</th>
                <th className="px-4 py-3 text-center">Plata</th>
                <th className="px-4 py-3 text-center">Bronce</th>
                <th className="px-4 py-3 text-center">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {summary.length ? (
                summary.map((row) => (
                  <tr key={`${row.rank}-${row.institution?.id || row.institution?.name}`}>
                    <td className="px-4 py-3 font-bold text-gray-900">{row.rank}</td>
                    <td className="px-4 py-3 font-bold text-gray-900">{row.institution?.name}</td>
                    <td className="px-4 py-3 text-center font-bold text-amber-700">{row.medals?.gold || 0}</td>
                    <td className="px-4 py-3 text-center font-bold text-slate-700">{row.medals?.silver || 0}</td>
                    <td className="px-4 py-3 text-center font-bold text-orange-700">{row.medals?.bronze || 0}</td>
                    <td className="px-4 py-3 text-center font-bold text-gray-900">{row.medals?.total || 0}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm font-semibold text-gray-500">
                    Todavía no hay medallas guardadas para este evento.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default MedalSportEditor;
